import { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';
import { Plus, Edit2, Trash2, Flame, Save, X, Upload } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function AdminWicks({ onLogout }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    image_url: '',
    scent: '',
    size: '',
    burn_time: '',
    price: 18.00,
    active: true,
    sort_order: 0,
    stock: null
  });

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get('/admin/wicks-products');
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const uploadFormData = new FormData();
    uploadFormData.append('file', file);

    try {
      const response = await axios.post('/upload/image', uploadFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData({ ...formData, image_url: response.data.url });
      toast.success('Image uploaded');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingProduct) {
        await axios.put(`/admin/wicks-products/${editingProduct.id}`, formData);
        toast.success('Product updated');
      } else {
        await axios.post('/admin/wicks-products', formData);
        toast.success('Product created');
      }
      
      fetchProducts();
      resetForm();
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error('Failed to save product');
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      title: product.title || '',
      description: product.description || '',
      image_url: product.image_url || '',
      scent: product.scent || '',
      size: product.size || '',
      burn_time: product.burn_time || '',
      price: product.price || 18.00,
      active: product.active !== false,
      sort_order: product.sort_order || 0,
      stock: product.stock ?? null
    });
    setShowForm(true);
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this candle?')) return;

    try {
      await axios.delete(`/admin/wicks-products/${productId}`);
      toast.success('Product deleted');
      fetchProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error('Failed to delete product');
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingProduct(null);
    setFormData({
      title: '',
      description: '',
      image_url: '',
      scent: '',
      size: '',
      burn_time: '',
      price: 18.00,
      active: true,
      sort_order: 0,
      stock: null
    });
  };

  return (
    <AdminLayout onLogout={onLogout} activeItem="wicks">
      <div className="page-header">
        <h1>B.B. Wicks - Candles</h1>
        <button className="btn btn-primary" onClick={() => setShowForm(true)}>
          <Plus size={18} /> Add Candle
        </button>
      </div>

      {/* Product Form Modal */}
      {showForm && (
        <div className="card" style={{ marginBottom: '30px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h2>{editingProduct ? 'Edit Candle' : 'Add New Candle'}</h2>
            <button onClick={resetForm} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
              <X size={24} />
            </button>
          </div>
          
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Candle Name *</label>
              <input
                type="text"
                className="form-input"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                placeholder="e.g., Ocean Breeze"
              />
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea
                className="form-textarea"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe the candle..."
                style={{ minHeight: '100px' }}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <div className="form-group">
                <label>Scent</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.scent}
                  onChange={(e) => setFormData({ ...formData, scent: e.target.value })}
                  placeholder="e.g., Sea Salt & Driftwood"
                />
              </div>

              <div className="form-group">
                <label>Size</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.size}
                  onChange={(e) => setFormData({ ...formData, size: e.target.value })}
                  placeholder="e.g., 8 oz"
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <div className="form-group">
                <label>Burn Time</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.burn_time}
                  onChange={(e) => setFormData({ ...formData, burn_time: e.target.value })}
                  placeholder="e.g., 40-50 hours"
                />
              </div>

              <div className="form-group">
                <label>Price ($) *</label>
                <input
                  type="number"
                  step="0.01"
                  className="form-input"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) })}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label>Image</label>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <input
                  type="text"
                  className="form-input"
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  placeholder="Image URL or upload below"
                  style={{ flex: 1 }}
                />
                <label className="btn btn-secondary" style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <Upload size={16} />
                  {uploading ? 'Uploading...' : 'Upload'}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    style={{ display: 'none' }}
                  />
                </label>
              </div>
              {formData.image_url && (
                <img 
                  src={formData.image_url} 
                  alt="Preview" 
                  style={{ marginTop: '10px', maxWidth: '200px', borderRadius: '8px' }}
                />
              )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <div className="form-group">
                <label>Sort Order</label>
                <input
                  type="number"
                  className="form-input"
                  value={formData.sort_order}
                  onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) })}
                />
              </div>

              <div className="form-group">
                <label>Quantity in Stock</label>
                <input
                  type="number"
                  min="0"
                  className="form-input"
                  value={formData.stock ?? ''}
                  onChange={(e) => setFormData({ ...formData, stock: e.target.value === '' ? null : parseInt(e.target.value, 10) })}
                  placeholder="Unlimited"
                />
              </div>
            </div>

            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <input
                  type="checkbox"
                  checked={formData.active}
                  onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                  style={{ width: '20px', height: '20px' }}
                />
                Active (visible in shop)
              </label>
            </div>

            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
              <button type="submit" className="btn btn-primary">
                <Save size={18} /> {editingProduct ? 'Update' : 'Create'} Candle
              </button>
              <button type="button" className="btn btn-secondary" onClick={resetForm}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Products List */}
      {loading ? (
        <div className="loading-screen">
          <div className="loading-spinner"></div>
        </div>
      ) : products.length === 0 ? (
        <div className="empty-state">
          <Flame size={48} strokeWidth={1} />
          <h3>No candles yet</h3>
          <p>Add your first candle to get started</p>
        </div>
      ) : (
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Image</th>
                <th>Name</th>
                <th>Scent</th>
                <th>Price</th>
                <th>Stock</th>
                <th>Active</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.map(product => (
                <tr key={product.id}>
                  <td>
                    {product.image_url ? (
                      <img 
                        src={product.image_url} 
                        alt={product.title}
                        style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '8px' }}
                      />
                    ) : (
                      <div style={{ width: '60px', height: '60px', background: '#f0f0f0', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Flame size={24} color="#ccc" />
                      </div>
                    )}
                  </td>
                  <td>
                    <strong>{product.title}</strong>
                    {product.size && <div style={{ fontSize: '13px', color: '#666' }}>{product.size}</div>}
                  </td>
                  <td>{product.scent || '-'}</td>
                  <td>${product.price?.toFixed(2)}</td>
                  <td style={{ color: product.stock === 0 ? '#dc3545' : 'inherit' }}>
                    {product.stock === null || product.stock === undefined ? '∞' : product.stock}
                  </td>
                  <td>
                    <span style={{
                      padding: '4px 12px',
                      borderRadius: '20px',
                      fontSize: '12px',
                      background: product.active ? '#d4edda' : '#f8d7da',
                      color: product.active ? '#155724' : '#721c24'
                    }}>
                      {product.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <button
                        onClick={() => handleEdit(product)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1a1a1a' }}
                        title="Edit"
                      >
                        <Edit2 size={18} />
                      </button>
                      <button
                        onClick={() => handleDelete(product.id)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc3545' }}
                        title="Delete"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
}

export default AdminWicks;
